import type { LucideIcon } from "lucide-react";
import {
  BookOpen,
  ChartLine,
  ChevronLeft,
  ClipboardCheck,
  Database,
  Home,
  LayoutGrid,
  MessageCircle,
  PencilLine,
  Plus,
  RefreshCw,
  Repeat,
  ShieldCheck,
  Sparkles,
  Target,
  Wrench,
} from "lucide-react";
import { Card } from "@/components/ui/Card";
import { cn } from "@/lib/cn";

export interface GuideProps {
  onSwitchScreen?: (screen: string) => void;
}

interface ToolEntry {
  readonly name: string;
  readonly description: string;
}

interface ToolGroup {
  readonly title: string;
  readonly note?: string;
  readonly tools: readonly ToolEntry[];
}

const DAILY_LOOP: readonly { icon: LucideIcon; title: string; line: string }[] = [
  {
    icon: PencilLine,
    title: "Log",
    line: "Capture meals, weight, and exercise by text, photo, or voice.",
  },
  {
    icon: ClipboardCheck,
    title: "Review",
    line: "Check each estimate in the meal-review card before it is logged.",
  },
  {
    icon: ChartLine,
    title: "Track",
    line: "Watch daily totals, weight trends, and progress build up.",
  },
  {
    icon: RefreshCw,
    title: "Adapt",
    line: "The plan engine adjusts your targets as your data improves.",
  },
];

const FIVE_SCREENS: readonly { icon: LucideIcon; title: string; line: string }[] = [
  {
    icon: MessageCircle,
    title: "Chat",
    line: "Your conversational home — ask anything and log in natural language.",
  },
  {
    icon: Home,
    title: "Today",
    line: "Everything you logged today, grouped by meal, with your remaining budget.",
  },
  {
    icon: Plus,
    title: "Quick Log",
    line: "The fastest capture routes — photo, voice, barcode, search, and more.",
  },
  {
    icon: ChartLine,
    title: "Progress",
    line: "Weekly summaries, weight trends, workouts, and day-by-day history.",
  },
  {
    icon: Target,
    title: "Plan",
    line: "Your programme: targets, forecasts, expenditure, and check-ins.",
  },
];

const AGENT_PROMPTS: readonly string[] = [
  "Log meals by text, photo, or voice note.",
  "Estimate calories and macros for any food or recipe.",
  "Log your weight and exercise sessions.",
  "Save and reuse food memories for one-tap re-logging.",
  "Ask about your plan, progress, or daily calorie expenditure.",
  "Handle weekly check-ins when a plan update is ready.",
  "Search the web for foods and nutrition facts.",
];

const TOOL_GROUPS: readonly ToolGroup[] = [
  {
    title: "Estimates & analysis",
    tools: [
      { name: "estimateFoodText", description: "Estimates calories and macros from a meal description." },
      { name: "estimateFoodImage", description: "Estimates nutrition from a photo of food." },
      { name: "prepareMealReview", description: "Builds the meal-review card so you can check an estimate before logging." },
      { name: "estimateExercise", description: "Estimates calories burned for a described workout." },
      { name: "getTodaySummary", description: "Summarises today's logged food, exercise, and remaining budget." },
      { name: "getNutritionProfile", description: "Reads your daily targets and macro goals." },
      { name: "getNutritionContext", description: "Gathers your profile and recent logs to ground answers." },
    ],
  },
  {
    title: "Progress & plan engine",
    tools: [
      { name: "getEngineSnapshot", description: "Reads the current plan-engine state and targets." },
      { name: "recalculateProgress", description: "Re-runs the plan engine with the latest data." },
      { name: "getWeightTrend", description: "Turns your weigh-ins into a trend line." },
      { name: "getGoalTimeline", description: "Projects when you will reach your goal weight." },
      { name: "getPendingCheckin", description: "Shows any weekly plan update waiting for your decision." },
      { name: "getExpenditureInfo", description: "Explains your estimated daily calorie burn." },
      { name: "getProgrammeAdjustments", description: "Lists recent automatic target adjustments." },
      { name: "getDataQuality", description: "Rates how confident the engine is in your data." },
      { name: "getCalculationAudit", description: "Shows the step-by-step calculation behind your targets." },
      { name: "explainMetric", description: "Explains any plan metric in plain language." },
    ],
  },
  {
    title: "Logging & writes",
    note: "Approval-gated: the agent proposes, you confirm.",
    tools: [
      { name: "logMealItems", description: "Logs confirmed meal items to your diary." },
      { name: "logExercise", description: "Logs a workout to your diary." },
      { name: "updateEntry", description: "Edits an existing food or exercise entry." },
      { name: "deleteEntry", description: "Removes an entry from your diary." },
      { name: "logWeight", description: "Records a new weigh-in." },
      { name: "markDayStatus", description: "Marks a day as complete, fasting, or skipped." },
      { name: "updateProgrammeGoal", description: "Changes your goal or programme targets." },
      { name: "updateProfileSettings", description: "Updates profile details such as units or timezone." },
    ],
  },
  {
    title: "Memories & search",
    tools: [
      { name: "searchFoodMemory", description: "Finds saved foods and meals by name." },
      { name: "listFoodMemories", description: "Lists your saved food memories." },
      { name: "saveFoodMemory", description: "Saves a food or meal for quick reuse." },
      { name: "updateFoodMemory", description: "Edits a saved memory." },
      { name: "deleteFoodMemory", description: "Deletes a saved memory." },
      { name: "searchLogbook", description: "Searches your past logged entries." },
    ],
  },
  {
    title: "Utilities",
    tools: [
      { name: "calculator", description: "Does exact arithmetic for nutrition maths." },
    ],
  },
];

const DATA_SOURCES: readonly { title: string; line: string }[] = [
  { title: "Verified database", line: "OpenFoodFacts entries with checked nutrition." },
  { title: "Barcode", line: "Packaged foods looked up straight from the barcode." },
  { title: "Nutrition label", line: "Values scanned from a photo of the label." },
  { title: "AI estimate", line: "Model estimates from your text, photo, or voice." },
  { title: "User-entered", line: "Numbers you typed in yourself." },
  { title: "Saved memory or recipe", line: "Foods and recipes you saved for reuse." },
];

function SectionHeader({ icon: Icon, title }: { icon: LucideIcon; title: string }) {
  return (
    <div className="flex items-center gap-2">
      <Icon size={20} className="text-text-muted" />
      <h3 className="text-base font-semibold text-text">{title}</h3>
    </div>
  );
}

export default function Guide({ onSwitchScreen }: GuideProps) {
  return (
    <div data-testid="guide-screen" className="flex flex-col gap-6 pb-8">
      <button
        type="button"
        data-testid="guide-back-btn"
        disabled={onSwitchScreen === undefined}
        onClick={() => {
          console.log("[Frontend][Guide] Returning to settings");
          onSwitchScreen?.("settings");
        }}
        className={cn(
          "flex min-h-[44px] items-center gap-1 self-start text-sm font-medium text-accent",
          "select-none transition-opacity active:opacity-80 disabled:opacity-50",
        )}
      >
        <ChevronLeft size={18} />
        Back to Settings
      </button>

      <div className="flex items-center gap-2">
        <BookOpen size={22} className="text-accent" />
        <h2 className="text-lg font-semibold text-text">How BiteTrack works</h2>
      </div>

      <Card data-testid="guide-daily-loop" className="flex flex-col gap-4 p-5">
        <SectionHeader icon={Repeat} title="The daily loop" />
        <p className="text-sm leading-relaxed text-text-muted">
          Log → Review → Track → Adapt. Four small steps, repeated daily.
        </p>
        <ol className="flex flex-col gap-3">
          {DAILY_LOOP.map((step, index) => (
            <li key={step.title} className="flex items-start gap-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-light text-accent">
                <step.icon size={16} />
              </span>
              <p className="text-sm leading-relaxed text-text">
                <span className="font-semibold">
                  {index + 1}. {step.title}
                </span>
                {" — "}
                <span className="text-text-muted">{step.line}</span>
              </p>
            </li>
          ))}
        </ol>
      </Card>

      <Card data-testid="guide-screens" className="flex flex-col gap-4 p-5">
        <SectionHeader icon={LayoutGrid} title="The five screens" />
        <ul className="flex flex-col gap-3">
          {FIVE_SCREENS.map((screen) => (
            <li key={screen.title} className="flex items-start gap-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-text-muted">
                <screen.icon size={16} />
              </span>
              <p className="text-sm leading-relaxed text-text">
                <span className="font-semibold">{screen.title}</span>
                {" — "}
                <span className="text-text-muted">{screen.line}</span>
              </p>
            </li>
          ))}
        </ul>
      </Card>

      <Card data-testid="guide-agent-prompts" className="flex flex-col gap-4 p-5">
        <SectionHeader icon={Sparkles} title="What you can ask the chat agent" />
        <ul className="flex flex-col gap-2">
          {AGENT_PROMPTS.map((prompt) => (
            <li key={prompt} className="flex items-start gap-2 text-sm leading-relaxed text-text">
              <span aria-hidden="true" className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
              {prompt}
            </li>
          ))}
        </ul>
      </Card>

      <Card data-testid="guide-tools" className="flex flex-col gap-4 p-5">
        <SectionHeader icon={Wrench} title="Agent tool reference" />
        <p className="text-sm leading-relaxed text-text-muted">
          For power users and developers — every tool the chat agent can call,
          grouped by what it does.
        </p>
        {TOOL_GROUPS.map((group) => (
          <div key={group.title} className="flex flex-col gap-2">
            <div>
              <h4 className="text-sm font-semibold text-text">{group.title}</h4>
              {group.note !== undefined && (
                <p className="text-xs leading-relaxed text-text-muted">{group.note}</p>
              )}
            </div>
            <ul className="flex flex-col gap-2">
              {group.tools.map((tool) => (
                <li key={tool.name} className="text-sm leading-relaxed">
                  <code className="rounded-md bg-muted px-1.5 py-0.5 text-xs font-semibold text-text">
                    {tool.name}
                  </code>
                  <span className="text-text-muted"> — {tool.description}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </Card>

      <Card data-testid="guide-approvals" className="flex flex-col gap-4 p-5">
        <SectionHeader icon={ShieldCheck} title="Approvals" />
        <p className="text-sm leading-relaxed text-text-muted">
          Write actions proposed by the agent — logging meals, changing weights,
          editing your plan — pause for your approval before anything is saved.
          The meal-estimate card is the exception: its Log button logs the
          reviewed items directly.
        </p>
      </Card>

      <Card data-testid="guide-data-sources" className="flex flex-col gap-4 p-5">
        <SectionHeader icon={Database} title="Data sources & provenance" />
        <ul className="flex flex-col gap-2">
          {DATA_SOURCES.map((source) => (
            <li key={source.title} className="flex items-start gap-2 text-sm leading-relaxed">
              <span aria-hidden="true" className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
              <span className="text-text">
                <span className="font-semibold">{source.title}</span>
                {" — "}
                <span className="text-text-muted">{source.line}</span>
              </span>
            </li>
          ))}
        </ul>
        <div className="rounded-xl bg-muted px-4 py-3">
          <p className="text-sm leading-relaxed text-text">
            Every logged item keeps its source. Unknown nutrition is never shown
            as zero — it stays unknown until better data arrives.
          </p>
        </div>
      </Card>
    </div>
  );
}
